/**
 * one-stop/session — the portal customer session, and the sign-in surface.
 *
 * WHY THIS IS ITS OWN MODULE AND NOT PART OF `actions.tsx`
 * `actions.tsx` is the FOUR PUBLIC FLOWS a visitor can run without an account
 * (join, book, register, look up). Session ENTRY is a different thing: it runs
 * before there is a visitor at all, it resolves against a different base (the
 * identity worker, not the events worker), and it is the one place in this
 * package that touches a CREDENTIAL. Keeping it separate leaves the CTA seam
 * exactly as wide as it was, and gives the token one home.
 *
 * ⛔ THE TOKEN RULE, WHICH IS THE WHOLE POINT OF THIS FILE
 * The portal session token is an opaque bearer credential with a 4 h life. It
 * lives in the host screen's React state and — so that a reload inside the same
 * tab survives — in `sessionStorage`, under a key namespaced BY HOST. It is
 * never in a URL (not a query, not a hash, not a share link), never in
 * `localStorage`, never in a module-level singleton, and never rendered. The
 * contract says why: a session that can appear in a URL ends up in access logs,
 * the Referer header and browser history.
 *
 * WHAT BLOCKS GET TO SEE
 * `useOneStopSession()` hands a block the NON-SECRET facts only — the public
 * session id, the display name, whether this is a demo persona, and the contact
 * details the identity fragment released (`null` where it released none) — plus
 * the two callbacks the host implements. The token is not on that object and
 * reached from a block component.
 *
 * TWO ENTRIES, ONE SURFACE
 * The demo persona chooser and the email one-time code are the SAME sheet. The
 * tenant decides which one opens: `getPortalDemoPersonas` answers 200 with a
 * catalog on a tenant inside its demo window and the enumeration-safe 404
 * everywhere else — the contract makes "demo closed" and "no such route"
 * deliberately indistinguishable, so a 404 is not an error here. It is the
 * answer "there is no chooser on this tenant", and the email form opens instead.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { JSX, ReactNode } from "react";
import { Button, Input, Modal, OtpInput, SelectableTile } from "@v0y4ge/jrni-ui";
import { isApiError, portalSession } from "@v0y4ge/api-client";
import type { VisitorDetails } from "./actions";
import { uiStrings } from "./strings";
import type { StringKey } from "./strings";

/* ── the non-secret session, as a block sees it ──────────────────────────── */

/**
 * What a signed-in page knows about its viewer. `sessionId` and `displayName`
 * come off the page response's own `session` object; `demo` comes off the
 * establishing response's `demo` flag. NO credential, and no capability set: a
 * customer carries neither.
 */
export interface OneStopSession {
  /** `portal_customer_sessions.id`. Safe to log; not the credential. */
  readonly sessionId: string;
  readonly displayName: string | null;
  /** True only for a demo-persona session. Badged, never inferred client-side. */
  readonly demo: boolean;
  /**
   * The contact details the identity fragment released for this visitor, or
   * `null` — which is both "this tenant withholds them" and "this wire does not
   * carry them yet". Read, never derived. See `contactFromIdentity`.
   */
  readonly contact: OneStopContact | null;
}

/**
 * What a signed-in visitor should not have to retype, as the identity fragment
 * carries it (0019 docket §d, CC's addition in progress).
 *
 * EVERY MEMBER IS NULLABLE AND SO IS THE WHOLE OBJECT, because the fragment is
 * server-gated the way `showEid` already is: a tenant that does not want contact
 * details on the wire simply does not send them. Absent, never
 * delivered-and-hidden — so there is nothing here to mask client-side.
 */
export interface OneStopContact {
  readonly givenName: string | null;
  readonly familyName: string | null;
  readonly email: string | null;
  readonly phone: string | null;
}

/** A wire string worth using, or `null`. `""` and `"  "` are not values. */
function usableString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

/**
 * The contact details on an identity fragment, when the fragment carries them.
 *
 * ⚠️ BEHIND A PRESENCE CHECK, DELIBERATELY. `PortalIdentityFragment` has no
 * `contact` member in the contract this build compiles against — CC's addition is
 * in progress — so this reads through `unknown` rather than naming a field that
 * does not exist yet. The current contract typechecks, and the day the field
 * lands this function starts returning it with no other change: nothing below it
 * asks whether the wire was old or new, it asks whether there is a contact.
 *
 * ⛔ AND IT NEVER INVENTS ONE. No email is guessed from a name, no phone from an
 * EID, no local part from a display name. A fragment without contact details
 * yields `null`, and the form asks — which is the honest half of this gap.
 */
export function contactFromIdentity(fragmentData: unknown): OneStopContact | null {
  if (fragmentData === null || typeof fragmentData !== "object") return null;
  // Only the identity fragment. A `contact` on some other block's data is not
  // this contract's field and must not be read as if it were.
  if ((fragmentData as { kind?: unknown }).kind !== "identity") return null;
  const raw = (fragmentData as { contact?: unknown }).contact;
  if (raw === null || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  const contact: OneStopContact = {
    givenName: usableString(record["givenName"]),
    familyName: usableString(record["familyName"]),
    email: usableString(record["email"]),
    phone: usableString(record["phone"]),
  };
  // An object whose every member is null carries nothing; say so as `null`.
  const carriesSomething =
    contact.givenName !== null ||
    contact.familyName !== null ||
    contact.email !== null ||
    contact.phone !== null;
  return carriesSomething ? contact : null;
}

/** The establishing result, token included. Held ONLY by the host screen. */
export interface PortalSessionHandle {
  readonly token: string;
  readonly session: OneStopSession;
}

/**
 * The session seam, mirroring `OneStopActions`: the blocks are written against
 * THIS and nothing else, so the live implementation lands in the host screen and
 * not a line of any block component knows how a session is established.
 */
export interface OneStopSessionState {
  /** `null` on the anonymous page and in the Journey Builder preview. */
  readonly session: OneStopSession | null;
  /** False wherever nothing may be called — the builder preview. */
  readonly canSignIn: boolean;
  /** The host stores the token and re-reads the page in its signed-in flavor. */
  readonly onSignedIn: (handle: PortalSessionHandle) => void;
  /** The host revokes, discards its copy of the token, and re-reads the public page. */
  readonly onSignOut: () => void;
  /**
   * Set when a session-bearing read was refused and the viewer was put back on
   * the public page. DATA, so the panel can say what happened instead of the
   * page silently changing under them.
   */
  readonly notice: "sessionEnded" | null;
}

const noop = (): void => {};

/** No provider ⇒ no session and no entry. The builder preview's shape exactly. */
const NO_SESSION: OneStopSessionState = {
  session: null,
  canSignIn: false,
  onSignedIn: noop,
  onSignOut: noop,
  notice: null,
};

const SessionContext = createContext<OneStopSessionState | null>(null);

export function OneStopSessionProvider(props: {
  value: OneStopSessionState;
  children: ReactNode;
}): JSX.Element {
  return (
    <VisitorMemoryProvider>
      <SessionBridge value={props.value}>{props.children}</SessionBridge>
    </VisitorMemoryProvider>
  );
}

/**
 * The session context, with ONE thing added on the way through: sign-out also
 * forgets the remembered details.
 *
 * It is wrapped HERE rather than asked of the host because forgetting is not the
 * host's business — the host owns the credential, this module owns what the
 * visitor typed — and because a second callback on `OneStopSessionState` would be
 * a second thing every host has to remember to wire. A block still calls
 * `onSignOut()` and knows nothing about either.
 */
function SessionBridge(props: {
  value: OneStopSessionState;
  children: ReactNode;
}): JSX.Element {
  const memory = useVisitorMemory();
  const hostSignOut = props.value.onSignOut;
  const forget = memory.forget;
  const onSignOut = useCallback(() => {
    forget();
    hostSignOut();
  }, [forget, hostSignOut]);
  const bridged = useMemo<OneStopSessionState>(
    () => ({ ...props.value, onSignOut }),
    [props.value, onSignOut],
  );
  return <SessionContext.Provider value={bridged}>{props.children}</SessionContext.Provider>;
}

/** The session state in force. Never throws, never null. */
export function useOneStopSession(): OneStopSessionState {
  return useContext(SessionContext) ?? NO_SESSION;
}

/* ── where the token lives ──────────────────────────────────────────── */

/**
 * NAMESPACED BY HOST, deliberately. Two tenants previewed on one origin must not
 * be able to read each other's token out of one shared key, and the portal is
 * served on the tenant's own host, so the host IS the namespace — the same
 * convention the composer already uses for its `<slug>:edge-session` key.
 */
function storageKey(): string {
  const host = typeof location === "undefined" ? "" : location.host;
  return `${host}:portal-session`;
}

/**
 * Every accessor is wrapped: `sessionStorage` THROWS on access in a browser with
 * site data blocked and in some embedded contexts, and a page that cannot read a
 * token must render signed-out, not crash.
 */
export function readStoredToken(): string | null {
  try {
    const value = sessionStorage.getItem(storageKey());
    return value && value.trim() ? value : null;
  } catch {
    return null;
  }
}

export function writeStoredToken(token: string): void {
  try {
    sessionStorage.setItem(storageKey(), token);
  } catch {
    // A tab that cannot persist still has the token in memory for this view.
  }
}

export function clearStoredToken(): void {
  try {
    sessionStorage.removeItem(storageKey());
  } catch {
    // Nothing to clear is the same outcome as cleared.
  }
}

/* ── what the visitor already typed, once ────────────────────────────────── */

/**
 * ONE RECORD OF WHAT THE VISITOR TOLD US, so a student who joins a line, books a
 * slot and registers for a talk types their name and address ONCE instead of
 * three times.
 *
 * It is not a credential and it is not an account: it is the four fields already
 * on screen, kept for the length of this tab so the next form can start filled in
 * instead of empty.
 *
 * WHY IT FOLLOWS THE TOKEN'S RULES ANYWAY (0019 §b). It is the visitor's own name,
 * address and mobile number — PII — so it gets the same treatment and for the
 * same reasons: `sessionStorage`, NEVER `localStorage`, so it dies with the tab
 * rather than outliving the visit on a shared or public machine; namespaced BY
 * HOST, so one tenant's preview can never read another's; never in a URL, where
 * it would reach access logs, the `Referer` header and history; and every
 * accessor wrapped, because storage throws outright in a browser with site data
 * blocked and a form that cannot read a draft must render empty, not crash.
 *
 * WHAT IS NEVER STORED HERE: the session token (that has its own key and its own
 * rules), anything off a page document, and any field the visitor has not typed a
 * valid value into.
 */
function visitorStorageKey(): string {
  const host = typeof location === "undefined" ? "" : location.host;
  return `${host}:portal-visitor`;
}

const NO_DETAILS: VisitorDetails = { firstName: "", lastName: "", email: "", phone: "" };

/** A stored member is a string or it is nothing. Anything else is a corrupt key. */
function storedString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function carriesAnything(details: VisitorDetails): boolean {
  return (
    details.firstName !== "" ||
    details.lastName !== "" ||
    details.email !== "" ||
    details.phone !== ""
  );
}

/**
 * The remembered details, or `null` — for a fresh tab, a blocked store, and a key
 * that does not parse alike. FOUR KNOWN FIELDS ARE READ AND NOTHING ELSE: the key
 * is writable by anything running on this origin, so it is treated as input, not
 * as state we can trust.
 */
export function readStoredVisitor(): VisitorDetails | null {
  try {
    const raw = sessionStorage.getItem(visitorStorageKey());
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== "object") return null;
    const record = parsed as Record<string, unknown>;
    const details: VisitorDetails = {
      firstName: storedString(record["firstName"]),
      lastName: storedString(record["lastName"]),
      email: storedString(record["email"]),
      phone: storedString(record["phone"]),
    };
    return carriesAnything(details) ? details : null;
  } catch {
    return null;
  }
}

export function writeStoredVisitor(details: VisitorDetails): void {
  try {
    sessionStorage.setItem(visitorStorageKey(), JSON.stringify(details));
  } catch {
    // A tab that cannot persist still has the record in memory for this view.
  }
}

export function clearStoredVisitor(): void {
  try {
    sessionStorage.removeItem(visitorStorageKey());
  } catch {
    // Nothing to clear is the same outcome as cleared.
  }
}

/** The remembered details and the two things a form does to them. */
export interface VisitorMemory {
  /** `null` until the visitor has typed one valid field, in this tab. */
  readonly details: VisitorDetails | null;
  /**
   * Merge in what the visitor committed. ONLY NON-EMPTY VALUES ARE TAKEN: a
   * cleared field is not a correction to remember, and a form that wrote blanks
   * would erase the record every time it opened.
   */
  remember(patch: Partial<VisitorDetails>): void;
  /** Drop it, here and in storage. Sign-out does this with the token. */
  forget(): void;
}

const NO_MEMORY: VisitorMemory = { details: null, remember: noop, forget: noop };

const VisitorMemoryContext = createContext<VisitorMemory | null>(null);

/** Never throws, never null: no provider ⇒ nothing remembered and nothing stored. */
export function useVisitorMemory(): VisitorMemory {
  return useContext(VisitorMemoryContext) ?? NO_MEMORY;
}

/**
 * Mounted INSIDE `OneStopSessionProvider`, so both hosts get it without wiring a
 * second provider and no host file changed to turn this on.
 *
 * The write-through is an EFFECT, not a side effect inside the state updater:
 * updaters run twice under StrictMode and must stay pure, and persisting from the
 * effect also means the stored copy can never disagree with what the forms are
 * rendering.
 */
function VisitorMemoryProvider(props: { children: ReactNode }): JSX.Element {
  const [details, setDetails] = useState<VisitorDetails | null>(() => readStoredVisitor());

  useEffect(() => {
    if (details !== null) writeStoredVisitor(details);
  }, [details]);

  const remember = useCallback((patch: Partial<VisitorDetails>) => {
    setDetails((current) => {
      const base = current ?? NO_DETAILS;
      const next: VisitorDetails = {
        firstName: patch.firstName?.trim() || base.firstName,
        lastName: patch.lastName?.trim() || base.lastName,
        email: patch.email?.trim() || base.email,
        phone: patch.phone?.trim() || base.phone,
      };
      if (!carriesAnything(next)) return current;
      const same =
        current !== null &&
        next.firstName === current.firstName &&
        next.lastName === current.lastName &&
        next.email === current.email &&
        next.phone === current.phone;
      return same ? current : next;
    });
  }, []);

  const forget = useCallback(() => {
    clearStoredVisitor();
    setDetails(null);
  }, []);

  const memory = useMemo<VisitorMemory>(
    () => ({ details, remember, forget }),
    [details, remember, forget],
  );

  return (
    <VisitorMemoryContext.Provider value={memory}>{props.children}</VisitorMemoryContext.Provider>
  );
}

/* ── the session operations ──────────────────────────────────────────── */

export interface SignInPersona {
  readonly id: string;
  readonly displayName: string;
  readonly blurb: string | null;
  /** The address the demo login screen fills in.
   *
   *  DERIVED, and only ever for the demo chooser. `PortalDemoPersonaDto` is
   *  `.strict()` and carries `{id, display_name, blurb}` — no email — even
   *  though the seed table has the column, so an extra key on the wire would
   *  fail the parse rather than reach us. Asked for in
   *  CD-API-REQUEST-portal-demo-persona-email-2026-09-14.md; the moment the
   *  field lands this reads it and the derivation goes.
   *
   *  The screen SAYS these are demo credentials, which is what keeps a derived
   *  address honest: it is a prop on a prop, not a claim about a real mailbox. */
  readonly demoEmail: string;
}

/** `Jordan Ellis` → `jordan.ellis@<demo domain>`. One place, so the address the
 *  screen shows and the address anyone reads in the seed can be compared. */
function demoEmailFor(displayName: string): string {
  const local = displayName
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/gu, "")
    .replace(/[^a-z0-9]+/gu, ".")
    .replace(/^\.+|\.+$/gu, "");
  return `${local || "student"}@student.example.edu`;
}

export interface SignInCodePolicy {
  readonly expiresInSeconds: number;
  readonly resendAfterSeconds: number;
}

/**
 * The status a refusal carried, or `null` when it was not one.
 *
 * `isApiError`, never `instanceof` — the client is bundled into several contexts
 * and `instanceof` answers `false` across a duplicated module instance.
 */
export function refusalStatus(err: unknown): number | null {
  return isApiError(err) && typeof err.status === "number" ? err.status : null;
}

/**
 * The tenant's demo personas, or an EMPTY LIST when there is no chooser here.
 *
 * The 404 is swallowed on purpose, and it is not a swallowed error: the contract
 * answers the route-absent 404 both for a closed demo window and for a tenant
 * that never had one, so "there is no chooser" is the only thing it can mean.
 * Surfacing it would put a scary refusal in front of every production tenant.
 */
export async function listSignInPersonas(): Promise<readonly SignInPersona[]> {
  try {
    const personas = await portalSession.getPortalDemoPersonas();
    return personas.map((persona) => ({
      id: persona.id,
      displayName: persona.display_name,
      blurb: persona.blurb,
      demoEmail: demoEmailFor(persona.display_name),
    }));
  } catch (err: unknown) {
    if (refusalStatus(err) === 404) return [];
    throw err;
  }
}

/** `{ token, session }` — the token comes back HERE and from nowhere else. */
function handleOf(data: {
  token: string;
  session: { session_id: string; display_name: string | null; demo: boolean };
}): PortalSessionHandle {
  return {
    token: data.token,
    session: {
      sessionId: data.session.session_id,
      displayName: data.session.display_name,
      demo: data.session.demo,
      // The credential-bearing DTO carries no PII and has no reason to. Contact
      // details arrive with the PAGE, on the identity fragment, or not at all.
      contact: null,
    },
  };
}

export async function signInAsPersona(personaId: string): Promise<PortalSessionHandle> {
  return handleOf(await portalSession.createPortalDemoSession(personaId));
}

export async function requestSignInCode(email: string): Promise<SignInCodePolicy> {
  const policy = await portalSession.requestPortalLoginCode(email);
  return {
    expiresInSeconds: policy.expires_in_seconds,
    resendAfterSeconds: policy.resend_after_seconds,
  };
}

export async function signInWithCode(email: string, code: string): Promise<PortalSessionHandle> {
  return handleOf(await portalSession.createPortalSession({ email, code }));
}

/**
 * Revoke the session. The caller discards its copy of the token whether this
 * resolves or rejects — a student who pressed sign out must not be left holding
 * a live credential because the network dropped.
 */
export async function signOutSession(token: string): Promise<void> {
  await portalSession.endPortalSession(token);
}

/* ── prefilling the CTA forms from the session ───────────────────────────── */

/**
 * The signed-in name, split for the two name inputs.
 *
 * ⚠️ A HEURISTIC ON A STRING THAT WAS NEVER STRUCTURED FOR IT, and it is named
 * here rather than hidden because the contract carries no given/family pair —
 * and no email at all — on a session (0019 docket §1(d)). The split is on the
 * LAST space, which is right for a two-part name and wrong for "Van der Berg",
 * so both inputs stay EDITABLE and nothing is locked. Email and phone are
 * deliberately NOT guessed: they are absent from the wire, and inventing them
 * would be worse than asking for them.
 */
export function splitDisplayName(displayName: string | null): {
  firstName: string;
  lastName: string;
} {
  const trimmed = (displayName ?? "").trim().replace(/\s+/gu, " ");
  if (!trimmed) return { firstName: "", lastName: "" };
  const cut = trimmed.lastIndexOf(" ");
  if (cut < 0) return { firstName: trimmed, lastName: "" };
  return { firstName: trimmed.slice(0, cut), lastName: trimmed.slice(cut + 1) };
}

/* ── the sign-in sheet ───────────────────────────────────────────────── */

type Mode =
  | { readonly kind: "loading" }
  /* The demo fork (operator, 2026-09-14). Two ways in, and they answer
     different questions: "show me what a student sees" wants the login screen a
     student would meet, while "let me get in as anyone" wants the list. The
     chooser used to be the only door, so the first question had no answer. */
  | { readonly kind: "demoChoice"; readonly personas: readonly SignInPersona[] }
  | { readonly kind: "credentials"; readonly persona: SignInPersona; readonly personas: readonly SignInPersona[] }
  | { readonly kind: "personas"; readonly personas: readonly SignInPersona[] }
  | { readonly kind: "email"; readonly policy: SignInCodePolicy | null }
  | { readonly kind: "chooserFailed" };

/**
 * The refusal, mapped onto ONE catalogue sentence — never the envelope's own
 * `message`, which is a developer-facing English string and untranslated by
 * construction.
 *
 * 404 gets one sentence for BOTH "no such persona" and "the demo window is
 * closed", because the contract answers them identically on purpose and a
 * client that distinguished them would be inventing the difference.
 */
function failureKey(err: unknown): StringKey {
  const status = refusalStatus(err);
  if (status === 401) return "signin.error.code";
  if (status === 404) return "signin.error.unavailable";
  if (status === 429) return "signin.error.tooMany";
  return "signin.error.generic";
}

/**
 * A DELIBERATELY LOOSE address check, the same one the visitor form uses and for
 * the same reason: it catches only what is certainly not an address, so the form
 * stops sending requests the server will certainly refuse without inventing a
 * second, stricter, invisible rule.
 */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/u;

/** What the demo login screen shows in the password field.
 *
 *  It authenticates nothing. This portal has no password operation — the demo
 *  sign-in underneath is `createPortalDemoSession(personaId)` — so the field
 *  exists to show a student the screen they would meet, and the sheet says
 *  "demo credentials" directly above it. Named as a constant rather than typed
 *  inline so nobody can mistake it for a secret that leaked into source. */
const DEMO_PASSWORD = "demo-password";

/**
 * One sheet, both entries. It opens on the persona chooser where the tenant has
 * one and on the email form everywhere else, and the visitor can cross from the
 * chooser to the email form at any time.
 *
 * `onSignedIn` receives the HANDLE, token included — the host screen stores it.
 * This component never writes storage itself, so there is exactly one place in
 * the app where a token is persisted.
 */
export function SignInDialog(props: {
  open: boolean;
  locale: string;
  onClose: () => void;
  onSignedIn: (handle: PortalSessionHandle) => void;
}): JSX.Element | null {
  const { open, locale, onClose, onSignedIn } = props;
  const t = uiStrings(locale);

  const [mode, setMode] = useState<Mode>({ kind: "loading" });
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<StringKey | null>(null);
  const [email, setEmail] = useState("");
  const [emailProblem, setEmailProblem] = useState<StringKey | null>(null);
  const [code, setCode] = useState("");
  const [waitSeconds, setWaitSeconds] = useState(0);
  const live = useRef(true);

  // Every open is a fresh sheet: a chooser left in a failed state, a half-typed
  // address and a spent code must not survive a close.
  useEffect(() => {
    if (!open) return;
    live.current = true;
    setMode({ kind: "loading" });
    setBusy(false);
    setFailure(null);
    setEmail("");
    setEmailProblem(null);
    setCode("");
    setWaitSeconds(0);
    listSignInPersonas().then(
      (personas) => {
        if (!live.current) return;
        // No chooser here ⇒ the email form IS the entry, not a fallback state.
        setMode(
          personas.length > 0 ? { kind: "demoChoice", personas } : { kind: "email", policy: null },
        );
      },
      () => {
        if (live.current) setMode({ kind: "chooserFailed" });
      },
    );
    return () => {
      live.current = false;
    };
  }, [open]);

  // The resend timer, counted down from the POLICY constant the server declared
  // — never a number this file picked.
  useEffect(() => {
    if (waitSeconds <= 0) return;
    const timer = setTimeout(() => setWaitSeconds((n) => Math.max(0, n - 1)), 1000);
    return () => clearTimeout(timer);
  }, [waitSeconds]);

  const choosePersona = useCallback(
    (personaId: string) => {
      if (busy) return;
      setBusy(true);
      setFailure(null);
      signInAsPersona(personaId).then(
        (handle) => {
          if (!live.current) return;
          setBusy(false);
          onSignedIn(handle);
        },
        (err: unknown) => {
          if (!live.current) return;
          setBusy(false);
          setFailure(failureKey(err));
        },
      );
    },
    [busy, onSignedIn],
  );

  const sendCode = useCallback(() => {
    if (busy) return;
    const address = email.trim();
    if (!EMAIL_SHAPE.test(address)) {
      setEmailProblem("visitor.error.email");
      return;
    }
    setEmailProblem(null);
    setBusy(true);
    setFailure(null);
    requestSignInCode(address).then(
      (policy) => {
        if (!live.current) return;
        setBusy(false);
        setMode({ kind: "email", policy });
        setWaitSeconds(policy.resendAfterSeconds);
      },
      (err: unknown) => {
        if (!live.current) return;
        setBusy(false);
        setFailure(failureKey(err));
      },
    );
  }, [busy, email]);

  const submitCode = useCallback(() => {
    if (busy || code.length < 4) return;
    setBusy(true);
    setFailure(null);
    signInWithCode(email.trim(), code).then(
      (handle) => {
        if (!live.current) return;
        setBusy(false);
        onSignedIn(handle);
      },
      (err: unknown) => {
        if (!live.current) return;
        setBusy(false);
        setCode("");
        setFailure(failureKey(err));
      },
    );
  }, [busy, code, email, onSignedIn]);

  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose} title={t("signin.title")} maxWidth={460}>
      <Modal.Body>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {mode.kind === "loading" ? (
            <span className="osr-soft">{t("signin.chooser.loading")}</span>
          ) : null}

          {mode.kind === "chooserFailed" ? (
            <>
              <span className="osr-cta-error jrni-error" role="status">
                {t("signin.chooser.error")}
              </span>
              <div className="osr-actions jrni-actions">
                <span className="osr-act jrni-action">
                  <Button
                    hierarchy="secondary"
                    onClick={() => setMode({ kind: "email", policy: null })}
                  >
                    {t("signin.useEmail")}
                  </Button>
                </span>
              </div>
            </>
          ) : null}

          {mode.kind === "demoChoice" ? (
            <>
              <span className="osr-soft">{t("signin.demo.body")}</span>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <SelectableTile
                  title={t("signin.demo.asStudent")}
                  description={t("signin.demo.asStudentHint").replace("{name}", mode.personas[0].displayName)}
                  showIndicator={false}
                  disabled={busy}
                  onSelect={() => setMode({ kind: "credentials", persona: mode.personas[0], personas: mode.personas })}
                />
                <SelectableTile
                  title={t("signin.demo.pickFromList")}
                  description={t("signin.demo.pickFromListHint")}
                  showIndicator={false}
                  disabled={busy}
                  onSelect={() => setMode({ kind: "personas", personas: mode.personas })}
                />
              </div>
              <button
                type="button"
                className="osr-linkbtn jrni-text-action"
                disabled={busy}
                onClick={() => setMode({ kind: "email", policy: null })}
              >
                {t("signin.useEmail")}
              </button>
            </>
          ) : null}

          {mode.kind === "credentials" ? (
            <>
              {/* The login screen a student would meet, filled in. It is the
                  DEMO one and says so: there is no password operation on this
                  portal at all — sign-in is the persona op underneath — so a
                  password field pretending otherwise would claim an auth
                  mechanism the product does not have. Filled, readable,
                  labelled, and read-only for the same reason: typing a
                  different password could not change the outcome, and a field
                  that accepts input it ignores is a lie with a cursor in it. */}
              <span className="osr-soft">{t("signin.demo.credentials")}</span>
              <Input
                type="email"
                inputMode="email"
                autoComplete="username"
                full
                readOnly
                label={t("signin.email.field")}
                value={mode.persona.demoEmail}
                disabled={busy}
                onChange={() => undefined}
              />
              <Input
                type="password"
                autoComplete="current-password"
                full
                readOnly
                label={t("signin.demo.password")}
                value={DEMO_PASSWORD}
                disabled={busy}
                onChange={() => undefined}
              />
              <div className="osr-actions jrni-actions">
                <span className="osr-act jrni-action">
                  <Button
                    hierarchy="primary"
                    full
                    disabled={busy}
                    onClick={() => choosePersona(mode.persona.id)}
                  >
                    {t("signin.demo.submit")}
                  </Button>
                </span>
              </div>
              {mode.personas.length > 1 ? (
                <button
                  type="button"
                  className="osr-linkbtn jrni-text-action"
                  disabled={busy}
                  onClick={() => setMode({ kind: "personas", personas: mode.personas })}
                >
                  {t("signin.demo.otherStudent")}
                </button>
              ) : null}
            </>
          ) : null}

          {mode.kind === "personas" ? (
            <>
              <span className="osr-soft">{t("signin.chooser.body")}</span>
              <div
                role="radiogroup"
                aria-label={t("signin.chooser.body")}
                style={{ display: "flex", flexDirection: "column", gap: 8 }}
              >
                {mode.personas.map((persona) => (
                  <SelectableTile
                    key={persona.id}
                    title={persona.displayName}
                    description={persona.blurb ?? undefined}
                    showIndicator={false}
                    disabled={busy}
                    /* From the LIST the student lands straight in their portal:
                       this door was chosen instead of the login screen, so
                       routing it through one would be answering a question the
                       visitor did not ask. */
                    onSelect={() => choosePersona(persona.id)}
                  />
                ))}
              </div>
              <button
                type="button"
                className="osr-linkbtn jrni-text-action"
                disabled={busy}
                onClick={() => setMode({ kind: "demoChoice", personas: mode.personas })}
              >
                {t("signin.demo.back")}
              </button>
              <button
                type="button"
                className="osr-linkbtn jrni-text-action"
                disabled={busy}
                onClick={() => setMode({ kind: "email", policy: null })}
              >
                {t("signin.useEmail")}
              </button>
            </>
          ) : null}

          {mode.kind === "email" && mode.policy === null ? (
            <>
              <span className="osr-soft">{t("signin.email.body")}</span>
              <Input
                type="email"
                inputMode="email"
                autoComplete="email"
                full
                label={t("signin.email.field")}
                value={email}
                disabled={busy}
                error={emailProblem ? t(emailProblem) : null}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setEmailProblem(null);
                }}
              />
            </>
          ) : null}

          {mode.kind === "email" && mode.policy !== null ? (
            <>
              {/*
                The SAME sentence for an address that resolves to a student and
                one that does not — the response carries nothing to tell them
                apart, and this copy must not invent the difference.
              */}
              <span className="osr-soft">
                {t("signin.email.sent", {
                  n: Math.max(1, Math.round(mode.policy.expiresInSeconds / 60)),
                })}
              </span>
              <span className="osr-panel-eyebrow jrni-eyebrow">{t("signin.email.code")}</span>
              <OtpInput value={code} onChange={setCode} error={failure !== null} />
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="osr-linkbtn jrni-text-action"
                  disabled={busy || waitSeconds > 0}
                  onClick={sendCode}
                >
                  {t("signin.email.resend")}
                </button>
                {waitSeconds > 0 ? (
                  <span className="osr-soft">{t("signin.email.wait", { n: waitSeconds })}</span>
                ) : null}
                <button
                  type="button"
                  className="osr-linkbtn jrni-text-action"
                  disabled={busy}
                  onClick={() => {
                    setMode({ kind: "email", policy: null });
                    setCode("");
                    setFailure(null);
                  }}
                >
                  {t("signin.email.other")}
                </button>
              </div>
            </>
          ) : null}

          {failure ? (
            <span className="osr-cta-error jrni-error" role="status">
              {t(failure)}
            </span>
          ) : null}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button hierarchy="tertiary" onClick={onClose} disabled={busy}>
          {t("visitor.cancel")}
        </Button>
        {mode.kind === "email" ? (
          <Button
            hierarchy="primary"
            disabled={busy}
            onClick={mode.policy === null ? sendCode : submitCode}
          >
            {busy
              ? t("cta.working")
              : mode.policy === null
                ? t("signin.email.send")
                : t("signin.email.submit")}
          </Button>
        ) : null}
      </Modal.Footer>
    </Modal>
  );
}
